import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import type { UniverseNode } from '../../../../types/targetUniverse';

// Reference colour palette — DO NOT CHANGE
const C = {
  panelBorder: '#1e293b',
  text: '#e2e8f0',
  textMuted: '#64748b',
  textDim: '#475569',
  accent: '#3b82f6',
  projectTarget: '#22d3ee',
  pathway: '#a78bfa',
  safe: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
};

function riskColorStr(selLog: number): string {
  if (selLog >= 2) return C.safe;
  if (selLog >= 1) return C.warning;
  return C.danger;
}

function riskLabel(selLog: number): string {
  if (selLog >= 2) return 'SAFE';
  if (selLog >= 1) return 'WATCH';
  return 'RISK';
}

interface Props {
  nodes: UniverseNode[];
  selectedTarget: string | null;
  onSelectTarget: (id: string | null) => void;
  projectPIC50: number;
}

// Virtual canvas coordinate space — copied from patent scatter (800×600)
const CANVAS_W = 800;
const CANVAS_H = 600;

interface PositionedNode extends UniverseNode {
  px: number;
  py: number;
}

const TargetUniverse2D: React.FC<Props> = ({ nodes, selectedTarget, onSelectTarget, projectPIC50 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredTarget, setHoveredTarget] = useState<string | null>(null);
  // Follow system theme toggle
  const [isDarkMode, setIsDarkMode] = useState(() => document.body.classList.contains('theme-dark'));
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains('theme-dark'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Track container size so canvas redraws when layout changes
  const [canvasSize, setCanvasSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  // Zoom controls — copied from patent scatter (lines 467-471)
  const [zoomLevel, setZoomLevel] = useState(1);
  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev * 1.2, 3));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev / 1.2, 0.5));
  const handleZoomReset = () => setZoomLevel(1);

  // Theme-aware colours (matching 3D view pattern)
  const tp = isDarkMode ? '#e2e8f0' : '#1e293b';   // text primary
  const td = isDarkMode ? '#475569' : '#94a3b8';   // text dim
  const bg = isDarkMode ? '#0f172a' : '#f8fafc';   // backgrounds
  const bd = isDarkMode ? '#1e293b' : '#e2e8f0';   // borders

  // Node radius proportional to compound count — range 6px to 14px
  const maxCompoundCount = useMemo(() => Math.max(1, ...nodes.map(n => n.compoundCount || 1)), [nodes]);
  const getNodeRadius = useCallback((node: UniverseNode): number => {
    if (node.isProjectTarget) return 10;
    const countNorm = (node.compoundCount || 1) / maxCompoundCount;
    return 6 + countNorm * 8; // 6–14px
  }, [maxCompoundCount]);

  // Node colour by risk
  const getNodeColor = useCallback((node: UniverseNode): string => {
    if (node.isProjectTarget) return C.projectTarget;
    const selLog = projectPIC50 - node.pIC50;
    return riskColorStr(selLog);
  }, [projectPIC50]);

  // Compute 2D radial positions — project target at centre, off-targets in Fibonacci spiral
  // PRIMARY factor: index-based spiral fills 20-80% of radius (guarantees spread)
  // SECONDARY factor: selectivity nudges distance ±10% (risk inward, safe outward)
  const positioned = useMemo<PositionedNode[]>(() => {
    const cx = CANVAS_W / 2;
    const cy = CANVAS_H / 2;
    const maxRadius = Math.min(cx, cy);
    const goldenAngle = 2.39996; // ~137.508° in radians
    const nonProject = nodes.filter(n => !n.isProjectTarget);
    const total = nonProject.length || 1;

    return nodes.map(node => {
      if (node.isProjectTarget) {
        return { ...node, px: cx, py: cy };
      }
      const idx = nonProject.indexOf(node);
      // Base spiral distance: spreads evenly from 20% to 80% of radius
      const baseDistance = 0.2 + (idx / total) * 0.6;
      // Selectivity nudge: risk (selLog<1) nudges inward, safe (selLog>=2) nudges outward
      const selLog = projectPIC50 - node.pIC50;
      const nudge = selLog >= 2 ? 0.1 : selLog >= 1 ? 0.0 : -0.1;
      const finalDistance = Math.max(0.15, Math.min(0.85, baseDistance + nudge));
      const angle = idx * goldenAngle;
      return {
        ...node,
        px: cx + finalDistance * maxRadius * Math.cos(angle),
        py: cy + finalDistance * maxRadius * Math.sin(angle),
      };
    });
  }, [nodes, projectPIC50]);

  // Selected node for inspector
  const selectedNode = useMemo(() => {
    if (!selectedTarget) return null;
    return positioned.find(n => n.id === selectedTarget) || null;
  }, [selectedTarget, positioned]);

  // Hovered node for tooltip
  const hoveredNode = useMemo(() => {
    if (!hoveredTarget) return null;
    return positioned.find(n => n.id === hoveredTarget) || null;
  }, [hoveredTarget, positioned]);

  // Pathway links between nodes sharing pathways
  const pathwayLinks = useMemo(() => {
    const links: { a: PositionedNode; b: PositionedNode; sharedPathways: string[] }[] = [];
    for (let i = 0; i < positioned.length; i++) {
      for (let j = i + 1; j < positioned.length; j++) {
        const shared = positioned[i].pathways.filter(p => positioned[j].pathways.includes(p));
        if (shared.length > 0) {
          links.push({ a: positioned[i], b: positioned[j], sharedPathways: shared });
        }
      }
    }
    return links;
  }, [positioned]);

  // Pathway territory circles — groups of 3+ targets sharing a pathway
  // Adapted from patent scatter territory circles (dashed circles around patent families)
  const pathwayTerritories = useMemo(() => {
    const pathwayMembers = new Map<string, PositionedNode[]>();
    positioned.forEach(node => {
      node.pathways.forEach(p => {
        if (!pathwayMembers.has(p)) pathwayMembers.set(p, []);
        pathwayMembers.get(p)!.push(node);
      });
    });

    const territories: { name: string; cx: number; cy: number; r: number; count: number }[] = [];
    pathwayMembers.forEach((members, name) => {
      if (members.length < 3) return;
      let sx = 0, sy = 0;
      members.forEach(m => { sx += m.px; sy += m.py; });
      const centX = sx / members.length;
      const centY = sy / members.length;
      let maxDist = 0;
      members.forEach(m => {
        const d = Math.sqrt((m.px - centX) ** 2 + (m.py - centY) ** 2);
        if (d > maxDist) maxDist = d;
      });
      territories.push({
        name,
        cx: centX, cy: centY,
        r: Math.min(Math.max(maxDist + 20, 30), 160),
        count: members.length,
      });
    });
    return territories;
  }, [positioned]);

  // Risk summary counts
  const riskCounts = useMemo(() => {
    let risk = 0, watch = 0, safe = 0, project = 0;
    nodes.forEach(n => {
      if (n.isProjectTarget) { project++; return; }
      const selLog = projectPIC50 - n.pIC50;
      if (selLog >= 2) safe++;
      else if (selLog >= 1) watch++;
      else risk++;
    });
    return { risk, watch, safe, project };
  }, [nodes, projectPIC50]);

  // ── ResizeObserver — detect when container gets real dimensions ──
  // This is critical: without it, the canvas draws at 0×0 on first mount
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setCanvasSize({ w: width, h: height });
        }
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // ── Canvas drawing — copied from patent scatter canvas approach ──
  // Patent scatter: line 1230–1581 (main canvas useEffect)
  useEffect(() => {
    if (!canvasRef.current || !containerRef.current || canvasSize.w === 0 || canvasSize.h === 0) return;

    // Theme-aware canvas colours
    const dk = isDarkMode;
    const cvBg = dk ? '#0f172a' : '#f8fafc';
    const guideColor = dk ? '#334155' : '#cbd5e0';
    const lblBg = dk ? 'rgba(15, 23, 42, 0.7)' : 'rgba(248, 250, 252, 0.85)';
    const lblText = dk ? C.text : '#1e293b';
    const legBg = dk ? 'rgba(17, 24, 39, 0.85)' : 'rgba(255, 255, 255, 0.92)';
    const legBorder = dk ? C.panelBorder : '#e2e8f0';
    const selRing = dk ? '#ffffff' : '#1e293b';
    const hoverRing = dk ? 'rgba(255, 255, 255, 0.5)' : 'rgba(30, 41, 59, 0.5)';
    const pathLblBg = dk ? 'rgba(15, 23, 42, 0.8)' : 'rgba(248, 250, 252, 0.85)';
    const pathLblColor = dk ? 'rgba(167, 139, 250, 0.7)' : 'rgba(126, 87, 194, 0.85)';
    const pathCountColor = dk ? 'rgba(148, 163, 184, 0.6)' : 'rgba(100, 116, 139, 0.8)';
    const pathLineAlpha = dk ? 0.08 : 0.12;
    const pathLineSelAlpha = dk ? 0.2 : 0.3;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Canvas DPR setup — copied from patent scatter (lines 1240-1246)
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasSize.w * dpr;
    canvas.height = canvasSize.h * dpr;
    canvas.style.width = `${canvasSize.w}px`;
    canvas.style.height = `${canvasSize.h}px`;
    ctx.scale(dpr, dpr);

    // Clear with background
    ctx.fillStyle = cvBg;
    ctx.fillRect(0, 0, canvasSize.w, canvasSize.h);

    // Scale factors — copied from patent scatter (lines 1256-1257)
    const scaleX = canvasSize.w / CANVAS_W;
    const scaleY = canvasSize.h / CANVAS_H;

    // ── Draw concentric guide rings at 25%, 50%, 75% radius ──
    const guideCx = (CANVAS_W / 2) * scaleX;
    const guideCy = (CANVAS_H / 2) * scaleY;
    const guideMaxR = Math.min(CANVAS_W / 2, CANVAS_H / 2) * Math.min(scaleX, scaleY);
    [0.25, 0.50, 0.75].forEach(frac => {
      ctx.beginPath();
      ctx.arc(guideCx, guideCy, guideMaxR * frac, 0, 2 * Math.PI);
      ctx.strokeStyle = guideColor;
      ctx.lineWidth = 0.5;
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
    });

    // ── Draw pathway territory dashed circles ──
    // Copied from patent scatter territory drawing (lines 1348-1412)
    pathwayTerritories.forEach(territory => {
      const x = territory.cx * scaleX;
      const y = territory.cy * scaleY;
      const r = territory.r * Math.min(scaleX, scaleY);

      ctx.beginPath();
      ctx.arc(x, y, r, 0, 2 * Math.PI);
      ctx.strokeStyle = 'rgba(167, 139, 250, 0.35)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Pathway label above circle — copied from patent scatter label pattern (lines 1371-1411)
      const label = territory.name.length > 28 ? territory.name.slice(0, 25) + '...' : territory.name;
      const labelY = y - r - 8;

      ctx.font = '9px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';

      // Semi-transparent background behind label — copied from patent scatter (lines 1378-1386)
      const textWidth = ctx.measureText(label).width;
      ctx.fillStyle = pathLblBg;
      ctx.fillRect(x - textWidth / 2 - 3, labelY - 8, textWidth + 6, 12);

      ctx.fillStyle = pathLblColor;
      ctx.fillText(label, x, labelY);

      // Count below label — copied from patent scatter (lines 1408-1411)
      ctx.font = '8px Inter, system-ui, sans-serif';
      ctx.fillStyle = pathCountColor;
      ctx.fillText(`${territory.count} targets`, x, labelY + 10);
    });

    // ── Draw pathway connection lines ──
    // Adapted from patent scatter connection lines (lines 1442-1510)
    pathwayLinks.forEach(link => {
      const x1 = link.a.px * scaleX;
      const y1 = link.a.py * scaleY;
      const x2 = link.b.px * scaleX;
      const y2 = link.b.py * scaleY;

      const isSelectedLink = selectedTarget === link.a.id || selectedTarget === link.b.id;
      const lineWidth = Math.min(3, 1 + link.sharedPathways.length * 0.5);

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.strokeStyle = isSelectedLink
        ? `rgba(167, 139, 250, ${Math.min(0.6, pathLineSelAlpha + link.sharedPathways.length * 0.1)})`
        : `rgba(167, 139, 250, ${Math.min(0.4, pathLineAlpha + link.sharedPathways.length * 0.06)})`;
      ctx.lineWidth = isSelectedLink ? lineWidth + 1 : lineWidth;
      ctx.stroke();
    });

    // ── Draw target nodes ──
    // Copied from patent scatter molecule dot rendering (lines 1512-1554)
    positioned.forEach(target => {
      const x = target.px * scaleX;
      const y = target.py * scaleY;
      const isProject = target.isProjectTarget;
      const isSelected = selectedTarget === target.id;
      const isHovered = hoveredTarget === target.id;
      const radius = getNodeRadius(target);
      const color = getNodeColor(target);
      const displayRadius = isSelected ? radius * 1.4 : isHovered ? radius * 1.2 : radius;

      // Glow effect — copied from patent scatter (lines 1528-1532)
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = isProject ? 25 : isSelected ? 20 : isHovered ? 15 : 10;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      if (isProject) {
        // Project target: filled solid circle — copied from patent scatter fill pattern (lines 1534-1537)
        ctx.beginPath();
        ctx.arc(x, y, displayRadius, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.85;
        ctx.fill();
        ctx.globalAlpha = 1;
      } else {
        // Off-targets: outlined circle (dashed if predicted)
        ctx.beginPath();
        ctx.arc(x, y, displayRadius, 0, 2 * Math.PI);
        if (!target.known) ctx.setLineDash([4, 3]);
        ctx.strokeStyle = color;
        ctx.lineWidth = isSelected ? 2.5 : 2;
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Selection ring — copied from patent scatter (lines 1542-1551)
      ctx.shadowBlur = 0;
      if (isSelected) {
        ctx.beginPath();
        ctx.arc(x, y, displayRadius + 3, 0, 2 * Math.PI);
        ctx.strokeStyle = selRing;
        ctx.lineWidth = 2;
        ctx.stroke();
      } else if (isHovered) {
        ctx.beginPath();
        ctx.arc(x, y, displayRadius + 2, 0, 2 * Math.PI);
        ctx.strokeStyle = hoverRing;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      ctx.restore();

      // Name label — always show for project target, hovered, selected, or small datasets
      const showLabel = isProject || isHovered || isSelected || positioned.length <= 20;
      if (showLabel) {
        ctx.font = isProject ? 'bold 11px Inter, system-ui, sans-serif' : '10px Inter, system-ui, sans-serif';
        ctx.textAlign = isProject ? 'center' : 'left';
        const labelX = isProject ? x : x + displayRadius + 4;
        const labelBaseY = isProject ? y - displayRadius - 6 : y + 3;
        const tw = ctx.measureText(target.name).width;

        // Background for readability — copied from patent scatter label background pattern (lines 1378-1386)
        ctx.fillStyle = lblBg;
        if (isProject) {
          ctx.fillRect(x - tw / 2 - 2, labelBaseY - 10, tw + 4, 14);
        } else {
          ctx.fillRect(labelX - 2, labelBaseY - 10, tw + 4, 14);
        }

        ctx.fillStyle = isProject ? C.projectTarget : lblText;
        ctx.fillText(target.name, labelX, labelBaseY);
      }
    });

    // ── Draw HIGHLIGHTED connections for selected target (most prominent) ──
    // Copied from patent scatter highlighted connections (lines 1556-1579)
    if (selectedTarget) {
      const selNode = positioned.find(n => n.id === selectedTarget);
      if (selNode) {
        pathwayLinks
          .filter(l => l.a.id === selectedTarget || l.b.id === selectedTarget)
          .forEach(link => {
            ctx.beginPath();
            ctx.moveTo(link.a.px * scaleX, link.a.py * scaleY);
            ctx.lineTo(link.b.px * scaleX, link.b.py * scaleY);
            ctx.strokeStyle = '#a78bfa';
            ctx.lineWidth = 3;
            ctx.setLineDash([6, 3]);
            ctx.stroke();
            ctx.setLineDash([]);
          });
      }
    }

    // ── Axis labels ──
    // X axis: "Selectivity" along the bottom
    ctx.save();
    ctx.font = 'bold 12px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillStyle = isDarkMode ? '#94a3b8' : '#64748b';
    ctx.fillText('Selectivity', canvasSize.w / 2, canvasSize.h - 8);
    ctx.restore();

    // Y axis: "pIC50 (Affinity)" along the left, rotated
    ctx.save();
    ctx.font = 'bold 12px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillStyle = isDarkMode ? '#94a3b8' : '#64748b';
    ctx.translate(14, canvasSize.h / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('pIC50 (Affinity)', 0, 0);
    ctx.restore();

    // ── Legend — drawn on canvas ──
    // Copied layout from patent scatter legend pattern
    const lx = 12, ly = 12, lw = 185, lh = 130, lr = 6;
    ctx.fillStyle = legBg;
    ctx.strokeStyle = legBorder;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(lx + lr, ly);
    ctx.lineTo(lx + lw - lr, ly);
    ctx.quadraticCurveTo(lx + lw, ly, lx + lw, ly + lr);
    ctx.lineTo(lx + lw, ly + lh - lr);
    ctx.quadraticCurveTo(lx + lw, ly + lh, lx + lw - lr, ly + lh);
    ctx.lineTo(lx + lr, ly + lh);
    ctx.quadraticCurveTo(lx, ly + lh, lx, ly + lh - lr);
    ctx.lineTo(lx, ly + lr);
    ctx.quadraticCurveTo(lx, ly, lx + lr, ly);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    const legendItems: { color: string; label: string; solid: boolean; dash: boolean; isLine?: boolean }[] = [
      { color: C.projectTarget, label: 'Project target', solid: true, dash: false },
      { color: C.danger, label: 'Off-target (risk)', solid: false, dash: false },
      { color: C.warning, label: 'Off-target (watch)', solid: false, dash: false },
      { color: C.safe, label: 'Off-target (safe)', solid: false, dash: false },
      { color: C.danger, label: 'Predicted (similarity)', solid: false, dash: true },
      { color: C.pathway, label: 'Shared pathway', solid: false, dash: false, isLine: true },
    ];
    legendItems.forEach((item, i) => {
      const iy = ly + 18 + i * 18;
      const ix = lx + 12;
      if (item.isLine) {
        ctx.beginPath();
        ctx.moveTo(ix, iy);
        ctx.lineTo(ix + 12, iy);
        ctx.strokeStyle = item.color;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.arc(ix + 6, iy, 5, 0, 2 * Math.PI);
        if (item.solid) {
          ctx.fillStyle = item.color;
          ctx.globalAlpha = 0.8;
          ctx.fill();
          ctx.globalAlpha = 1;
        } else {
          if (item.dash) ctx.setLineDash([2, 2]);
          ctx.strokeStyle = item.color;
          ctx.lineWidth = 1.5;
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }
      ctx.font = '10px Inter, system-ui, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillStyle = C.textMuted;
      ctx.fillText(item.label, ix + 18, iy + 4);
    });

  }, [positioned, pathwayLinks, pathwayTerritories, selectedTarget, hoveredTarget, canvasSize, getNodeRadius, getNodeColor, isDarkMode]);

  // Handle canvas click — copied from patent scatter handleCanvasClick (lines 1583-1657)
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const scaleX = rect.width / CANVAS_W;
    const scaleY = rect.height / CANVAS_H;

    // Check targets — distance check copied from patent scatter (lines 1614-1625)
    for (const target of positioned) {
      const dx = x - target.px * scaleX;
      const dy = y - target.py * scaleY;
      if (Math.sqrt(dx * dx + dy * dy) < 18) {
        onSelectTarget(selectedTarget === target.id ? null : target.id);
        return;
      }
    }

    onSelectTarget(null);
  }, [positioned, selectedTarget, onSelectTarget]);

  // Handle mouse move for hover — copied from patent scatter handleCanvasMove (lines 1659-1706)
  const handleCanvasMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const scaleX = rect.width / CANVAS_W;
    const scaleY = rect.height / CANVAS_H;

    // Distance check — copied from patent scatter (lines 1690-1702)
    for (const target of positioned) {
      const dx = x - target.px * scaleX;
      const dy = y - target.py * scaleY;
      if (Math.sqrt(dx * dx + dy * dy) < 18) {
        canvas.style.cursor = 'pointer';
        setHoveredTarget(target.id);
        return;
      }
    }

    canvas.style.cursor = 'crosshair';
    setHoveredTarget(null);
  }, [positioned]);

  return (
    <div style={{ display: 'flex', width: '100%', height: '100%', minHeight: 400, position: 'relative' }}>
      {/* Main canvas area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Risk summary strip — adapted from patent scatter zone summary strip (lines 1751-1830) */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '6px 16px',
          borderBottom: `1px solid ${bd}`, background: bg, flexShrink: 0,
        }}>
          {[
            { color: C.projectTarget, label: 'Project target', count: riskCounts.project },
            { color: C.danger, label: 'Risk (<10\u00D7)', count: riskCounts.risk },
            { color: C.warning, label: 'Watch (10\u2013100\u00D7)', count: riskCounts.watch },
            { color: C.safe, label: 'Safe (>100\u00D7)', count: riskCounts.safe },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.color }} />
              <span style={{ fontSize: 12, color: td }}>{item.label}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: tp }}>{item.count}</span>
            </div>
          ))}
          <div style={{ marginLeft: 'auto', fontSize: 12, color: td }}>
            {nodes.length} targets
          </div>
        </div>

        {/* Canvas container — same pattern as patent scatter (line 2230) */}
        <div ref={containerRef} style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 350 }}>
          <canvas
            ref={canvasRef}
            onClick={handleCanvasClick}
            onMouseMove={handleCanvasMove}
            className="w-full h-full cursor-crosshair"
            style={{
              transform: `scale(${zoomLevel})`,
              transformOrigin: 'center center',
              transition: 'transform 0.2s ease-out',
            }}
          />

          {/* Zoom Controls — copied from patent scatter (lines 2243-2269) */}
          <div className="absolute bottom-4 right-4 flex flex-col gap-1 rounded-lg p-1 shadow-lg z-20" style={{ backgroundColor: isDarkMode ? 'rgba(17,24,39,0.9)' : 'rgba(255,255,255,0.9)', border: `1px solid ${bd}` }}>
            <button
              onClick={handleZoomIn}
              className="w-8 h-8 flex items-center justify-center rounded transition-colors text-lg font-semibold"
              style={{ color: tp, background: 'transparent', border: 'none', cursor: 'pointer' }}
              title="Zoom in (20%)"
            >
              +
            </button>
            <button
              onClick={handleZoomReset}
              className="w-8 h-8 flex items-center justify-center rounded transition-colors text-xs font-medium"
              style={{ color: td, background: 'transparent', border: 'none', cursor: 'pointer' }}
              title="Reset zoom"
            >
              {Math.round(zoomLevel * 100)}%
            </button>
            <button
              onClick={handleZoomOut}
              className="w-8 h-8 flex items-center justify-center rounded transition-colors text-lg font-semibold"
              style={{ color: tp, background: 'transparent', border: 'none', cursor: 'pointer' }}
              title="Zoom out (20%)"
            >
              {'\u2212'}
            </button>
          </div>

          {/* Hover tooltip — copied from patent scatter tooltip (lines 2271-2297) */}
          {hoveredNode && containerRef.current && (
            <div
              className="absolute pointer-events-none rounded-lg px-3 py-2 shadow-xl z-10"
              style={{
                backgroundColor: isDarkMode ? '#1e293b' : 'rgba(255,255,255,0.95)',
                border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                left: Math.min(
                  hoveredNode.px * (canvasSize.w / CANVAS_W) + 15,
                  canvasSize.w - 200
                ),
                top: Math.max(
                  hoveredNode.py * (canvasSize.h / CANVAS_H) - 30,
                  10
                ),
              }}
            >
              <div className="text-sm font-medium" style={{ color: hoveredNode.isProjectTarget ? C.projectTarget : tp }}>
                {hoveredNode.name}
              </div>
              <div className="text-xs mt-1" style={{ color: td }}>
                {hoveredNode.activityNM < 1000
                  ? `${Math.round(hoveredNode.activityNM)} nM`
                  : `${(hoveredNode.activityNM / 1000).toFixed(1)} \u00B5M`}
                {' · '}pIC50 {hoveredNode.pIC50.toFixed(1)}
              </div>
              {!hoveredNode.isProjectTarget && (() => {
                const sl = projectPIC50 - hoveredNode.pIC50;
                const ratio = Math.pow(10, sl);
                return (
                  <div className="text-xs mt-1" style={{ color: riskColorStr(sl) }}>
                    {ratio > 1000 ? `${(ratio / 1000).toFixed(0)}K\u00D7` : `${ratio.toFixed(0)}\u00D7`} selectivity {'·'} {riskLabel(sl)}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Controls hint */}
          <div
            className="absolute bottom-4 left-4 text-xs pointer-events-none backdrop-blur rounded px-2 py-1"
            style={{ color: isDarkMode ? '#64748b' : '#94a3b8', backgroundColor: isDarkMode ? 'rgba(15,23,42,0.7)' : 'rgba(255,255,255,0.8)' }}
          >
            Click target for details
          </div>
        </div>
      </div>

      {/* ─── Right sidebar — Target Inspector ─── */}
      {/* Copied from TargetUniverse3D inspector panel (lines 533-642) */}
      {selectedNode ? (
        <div style={{
          width: 260, overflowY: 'auto', flexShrink: 0,
          backgroundColor: bg,
          borderLeft: `1px solid ${bd}`,
          fontFamily: "'JetBrains Mono', monospace",
          padding: 16, position: 'relative',
        }}>
          {/* Close button */}
          <button
            onClick={() => onSelectTarget(null)}
            style={{
              position: 'absolute', top: 8, right: 8,
              background: 'none', border: 'none', color: td,
              cursor: 'pointer', fontSize: 16, fontWeight: 700,
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            {'\u00D7'}
          </button>

          <div style={{
            fontSize: 15, fontWeight: 700,
            color: selectedNode.isProjectTarget ? '#22d3ee' : tp,
            marginBottom: 4, paddingRight: 20,
          }}>
            {selectedNode.name}
          </div>
          {selectedNode.fullName && selectedNode.fullName !== selectedNode.name && (
            <div style={{ color: td, fontSize: 10, marginBottom: 12 }}>{selectedNode.fullName}</div>
          )}

          {selectedNode.uniprot && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ color: td, fontSize: 9, marginBottom: 2 }}>UNIPROT</div>
              <a href={`https://www.uniprot.org/uniprot/${selectedNode.uniprot}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', fontSize: 12 }}>
                {selectedNode.uniprot}
              </a>
            </div>
          )}

          {/* Activity + Selectivity cards — copied from TargetUniverse3D (lines 553-576) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
            <div style={{ padding: 8, background: 'rgba(59,130,246,0.08)', borderRadius: 6, border: '1px solid rgba(59,130,246,0.15)' }}>
              <div style={{ color: td, fontSize: 9, marginBottom: 2 }}>ACTIVITY</div>
              <div style={{ color: '#3b82f6', fontSize: 15, fontWeight: 700 }}>
                {selectedNode.activityNM < 1000 ? `${Math.round(selectedNode.activityNM)} nM` : `${(selectedNode.activityNM / 1000).toFixed(1)} \u00B5M`}
              </div>
              <div style={{ color: td, fontSize: 9 }}>pIC50 = {selectedNode.pIC50.toFixed(1)}</div>
            </div>
            {!selectedNode.isProjectTarget && (() => {
              const sl = projectPIC50 - selectedNode.pIC50;
              const ratio = Math.pow(10, sl);
              const c = riskColorStr(sl);
              return (
                <div style={{ padding: 8, background: `${c}15`, borderRadius: 6, border: `1px solid ${c}30` }}>
                  <div style={{ color: td, fontSize: 9, marginBottom: 2 }}>SELECTIVITY</div>
                  <div style={{ color: c, fontSize: 15, fontWeight: 700 }}>
                    {ratio > 1000 ? `${(ratio / 1000).toFixed(0)}K\u00D7` : `${ratio.toFixed(0)}\u00D7`}
                  </div>
                  <div style={{ color: c, fontSize: 9, fontWeight: 600 }}>{riskLabel(sl)}</div>
                </div>
              );
            })()}
          </div>

          {/* Pathways — copied from TargetUniverse3D (lines 578-588) */}
          {selectedNode.pathways.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ color: td, fontSize: 9, marginBottom: 4 }}>PATHWAYS</div>
              {selectedNode.pathways.map(p => (
                <div key={p} style={{ padding: '3px 6px', marginBottom: 2, background: 'rgba(139,92,246,0.08)', borderRadius: 4, border: '1px solid rgba(139,92,246,0.15)', color: '#8b5cf6', fontSize: 10 }}>
                  {p}
                </div>
              ))}
            </div>
          )}

          {/* Source + Safety badges — copied from TargetUniverse3D (lines 590-600) */}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 9, background: selectedNode.known ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: selectedNode.known ? '#10b981' : '#ef4444' }}>
              {selectedNode.known ? 'Exact match' : 'Predicted (\u226580%)'}
            </span>
            {selectedNode.safetyFlag && (
              <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 9, background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>
                {'\u26A0'} {selectedNode.safetyFlag}
              </span>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TargetUniverse2D;
